import { Injectable, inject, signal, computed } from '@angular/core';
import { PocketbaseService } from './pocketbase.service';

/**
 * Shared interface for chunks in the review UI
 */
export interface ReviewChunk {
    id: string;
    index: number;
    heading: string;
    paragraph: string;
}

/**
 * Backend chunk structure (from PocketBase)
 */
export interface BackendChunk {
    index: number;
    heading: string;
    text?: string;
    paragraph?: string;
    schwrpnkt_index0?: string | number;
    schwrpnkt_index1?: string | number;
    schwrpnkt_index2?: string | number;
    schwrpnkt_index3?: string | number;
    similarity0?: number;
    similarity1?: string;
    similarity2?: number;
    similarity3?: number;
    annotations?: any[];
    [key: string]: any;
}

/**
 * Options for saving chunks
 */
export interface ChunkSaveOptions {
    collection: string;
    recordId: string;
    chunkField: string;
    setConfirmationFlag?: boolean;
    createPendingRequest?: boolean;
}

/**
 * Shared service for chunk review operations.
 * Manages state and business logic for chunk review UI across components.
 */
@Injectable({
    providedIn: 'root'
})
export class ChunkReviewService {
    private readonly pbService = inject(PocketbaseService);

    // === Centralized State ===
    private readonly _originalChunks = signal<ReviewChunk[]>([]);
    private readonly _draftChunks = signal<ReviewChunk[]>([]);
    private readonly _isReviewMode = signal(false);
    private readonly _isConfirming = signal(false);
    private readonly _isParsing = signal(false);
    private readonly _currentRecordId = signal<string | null>(null);

    // Public readonly signals
    readonly originalChunks = this._originalChunks.asReadonly();
    readonly draftChunks = this._draftChunks.asReadonly();
    readonly isReviewMode = this._isReviewMode.asReadonly();
    readonly isConfirming = this._isConfirming.asReadonly();
    readonly isParsing = this._isParsing.asReadonly();
    readonly currentRecordId = this._currentRecordId.asReadonly();

    // Computed: check if there are edits
    readonly hasEditsComputed = computed(() =>
        this.hasEdits(this._originalChunks(), this._draftChunks())
    );

    /** Store raw backend chunks for merging edits */
    private rawChunksMap = new Map<string, BackendChunk[]>();

    // === State Management ===

    /**
     * Initialize chunk review mode with parsed chunks
     */
    initializeReview(recordId: string, chunks: BackendChunk[]): void {
        this.setRawChunks(recordId, chunks);
        const normalized = this.normalizeChunks(chunks);
        this._originalChunks.set(normalized);
        this._draftChunks.set(normalized.map(c => ({ ...c })));
        this._isReviewMode.set(true);
        this._currentRecordId.set(recordId);
    }

    /**
     * Update draft chunks (called by UI on changes)
     */
    updateDraftChunks(chunks: ReviewChunk[]): void {
        this._draftChunks.set(chunks.map(c => ({ ...c })));
    }

    /**
     * Reset drafts to original (cancel edits)
     */
    cancelEdits(): void {
        this._draftChunks.set(this._originalChunks().map(c => ({ ...c })));
    }

    /**
     * Exit review mode and clear state
     */
    exitReviewMode(): void {
        const recordId = this._currentRecordId();
        if (recordId) {
            this.clearRawChunks(recordId);
        }
        this._isReviewMode.set(false);
        this._originalChunks.set([]);
        this._draftChunks.set([]);
        this._currentRecordId.set(null);
    }

    /**
     * Set parsing state
     */
    setIsParsing(value: boolean): void {
        this._isParsing.set(value);
    }

    // === Confirm and Save ===

    /**
     * Confirm chunks and save to PocketBase.
     * Returns the formatted chunks that were saved.
     */
    async confirmChunks(options: ChunkSaveOptions): Promise<BackendChunk[]> {
        const chunks = this._draftChunks();
        const recordId = options.recordId;

        this._isConfirming.set(true);

        try {
            // Merge edits with raw chunks
            const formattedChunks = this.mergeEdits(recordId, chunks);
            console.log('🔧 [ChunkReviewService] Saving chunks:', formattedChunks);

            // Save to PocketBase
            await this.saveChunks(formattedChunks, options);
            console.log('🔧 [ChunkReviewService] Chunks saved successfully');

            return formattedChunks;
        } finally {
            this._isConfirming.set(false);
        }
    }

    // === Raw Chunks Storage ===

    setRawChunks(recordId: string, chunks: BackendChunk[]): void {
        this.rawChunksMap.set(recordId, chunks);
    }

    getRawChunks(recordId: string): BackendChunk[] {
        return this.rawChunksMap.get(recordId) || [];
    }

    clearRawChunks(recordId: string): void {
        this.rawChunksMap.delete(recordId);
    }

    // === Normalization & Merge ===

    normalizeChunks(chunks: BackendChunk[]): ReviewChunk[] {
        return (chunks || []).map((chunk, idx) => {
            const paragraph = typeof chunk.paragraph === 'string'
                ? chunk.paragraph
                : (chunk.text ?? '');
            const index = typeof chunk.index === 'number' ? chunk.index : Number(chunk.index) || (idx + 1);
            return {
                id: this.createChunkId(index),
                index,
                heading: chunk.heading ?? '',
                paragraph
            };
        });
    }

    mergeEdits(recordId: string, editedChunks: ReviewChunk[]): BackendChunk[] {
        const rawChunks = this.getRawChunks(recordId);

        return editedChunks.map((chunk, idx) => {
            const rawChunk = rawChunks.find(r => r.index === chunk.index) || rawChunks[idx] || {};
            return {
                ...rawChunk,
                index: idx + 1,
                heading: chunk.heading,
                text: chunk.paragraph,
                paragraph: chunk.paragraph
            };
        });
    }

    hasEdits(original: ReviewChunk[], draft: ReviewChunk[]): boolean {
        if (original.length !== draft.length) return true;
        return original.some((chunk, idx) => {
            const d = draft[idx];
            return chunk.heading !== d?.heading || chunk.paragraph !== d?.paragraph;
        });
    }

    // === PocketBase Operations ===

    async saveChunks(chunks: BackendChunk[], options: ChunkSaveOptions): Promise<void> {
        const pb = this.pbService.getPocketBase();

        const updatePayload: Record<string, any> = {
            [options.chunkField]: chunks
        };

        if (options.setConfirmationFlag) {
            updatePayload['chunk_review_status'] = 'confirmed';
        }

        await pb.collection(options.collection).update(options.recordId, updatePayload);

        if (options.createPendingRequest) {
            await pb.collection('pending_requests').create({ request: options.recordId });
        }
    }

    async waitForChunks(requestId: string, maxAttempts = 60): Promise<BackendChunk[]> {
        const pb = this.pbService.getPocketBase();

        for (let i = 0; i < maxAttempts; i++) {
            await new Promise(resolve => setTimeout(resolve, 2000));

            const request = await pb.collection('requests').getOne(requestId);

            if (request?.['chunks'] && request['chunks'].length > 0) {
                return request['chunks'];
            }

            if (request?.['status'] === -1) {
                throw new Error('PDF parsing failed');
            }
        }

        throw new Error('Timeout waiting for chunks');
    }

    // === Helpers ===

    private createChunkId(seed: number): string {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return crypto.randomUUID();
        }
        return `chunk-${seed}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    }
}
